import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseDiagnosisButton } from './CloseDiagnosisButton';
import { GuideIllustration } from './GuideIllustration';
import {
  ClockCircleIcon,
  PauseButtonIcon,
  PlayButtonIcon,
} from '../../../components';

import type { RecordTest } from '../recordTests';

/**
 * 시작 전 가이드 카드(덱 p6) — 진단 **하나**를 시작하기 직전의 화면.
 *
 * 진단마다 한 장이고 `n / N`으로 어디쯤인지 알린다. 안내를 전부 앞에 몰아 보여주면
 * 세 번째 진단을 할 때쯤 첫 장에서 읽은 걸 기억해야 한다 — 그래서 각 진단 바로 앞에 둔다.
 */

interface TestGuideCardProps {
  test: RecordTest;
  /** 0-기준 순번. 화면에는 `n / N`으로 낸다. */
  index: number;
  total: number;
  onStart: () => void;
  /**
   * 이 진단이 실제로 녹음하는 시간(초). 없으면 "무제한".
   *
   * `test.limitSec`을 여기서 다시 읽지 않는다 — 노래는 레퍼런스가 준비되면 멜로디 길이가
   * 그 값을 대체하므로(`takeSpanSec`), 설정값을 그대로 쓰면 카드가 20초를 약속하고 기계는
   * 6초에 끊는다.
   */
  limitSec: number | null;
  /**
   * 이 진단이 들려줄 레퍼런스. 있으면 **부르기 전에** 들어볼 수 있다.
   *
   * 둘이 이 자리를 쓴다: 노래 진단의 멜로디 음원, 지속음 진단의 기준음 하나
   * (`SUSTAIN_TONE_URL`). 지속음에 미리듣기가 필요한 이유는 화면이 "라(A)를 내라"고
   * 요구하는데 그 음을 들려줄 자리가 없으면 사용자가 아무 음이나 내기 때문이다
   * (`.claude/20260907/DEBUG-16-57-35.md`).
   *
   * 미리듣기가 측정 화면이 아니라 여기 있는 이유: 노래 진단은 재생 시작 시각이 채점의 시간
   * 앵커라(`SongReferenceAudio`) 녹음 중에 되감아 다시 틀면 이미 부른 프레임이 엉뚱한
   * 음에 대응된다. 녹음 전인 이 화면이 "다시 듣기"를 안전하게 줄 수 있는 유일한 자리다.
   * 지속음은 앵커가 없지만 같은 자리를 쓴다 — 녹음 중에 스피커로 기준음이 흐르면 마이크가
   * 그걸 같이 듣고 f0로 잡는다.
   */
  previewUrl?: string;
}

/**
 * 레퍼런스 미리듣기 — 채점과 무관한 별개의 재생기다(앵커를 건드리지 않는다).
 *
 * 문구는 무엇을 트는지에 따라 갈린다(`labelKey`): 멜로디와 음 하나를 같은 말로 부르면
 * 지속음 화면이 있지도 않은 멜로디를 약속한다.
 */
const ReferencePreview = ({
  src,
  labelKey,
  stopLabelKey,
}: {
  src: string;
  labelKey: string;
  stopLabelKey: string;
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  // 화면을 떠나면(가이드 → 카운트다운) 소리도 같이 멈춘다 — 녹음 위로 멜로디가 흐르면
  // 마이크가 그걸 같이 듣는다.
  useEffect(() => () => ref.current?.pause(), []);

  const toggle = (): void => {
    const audio = ref.current;
    if (!audio) {
      return;
    }
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    audio.currentTime = 0;
    void audio.play().catch(() => undefined);
    setPlaying(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={playing}
        className="mt-4 flex min-h-[44px] w-fit items-center gap-2 rounded-full border border-border px-4 text-sm font-medium"
      >
        {playing ? (
          <PauseButtonIcon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <PlayButtonIcon className="h-4 w-4" aria-hidden="true" />
        )}
        {t(playing ? stopLabelKey : labelKey)}
      </button>
      <audio
        ref={ref}
        src={src}
        preload="auto"
        onEnded={() => setPlaying(false)}
        aria-hidden="true"
      />
    </>
  );
};

export const TestGuideCard = ({
  test,
  index,
  total,
  onStart,
  limitSec,
  previewUrl,
}: TestGuideCardProps) => {
  const { t } = useTranslation();
  // 제한이 없는 진단은 "무제한"이라고 말한다 — 칸을 비우면 아직 못 정한 것처럼 보인다.
  const duration =
    limitSec == null
      ? t('web.diagnosis.guide.unlimited')
      : t('web.diagnosis.guide.seconds', { value: Math.ceil(limitSec) });

  return (
    <div className="flex flex-1 flex-col">
      {/* 시안의 진단 헤더 — 가운데 `n/4`, 오른쪽 `×`. 왼쪽은 같은 폭의 빈 자리를 둬야
          숫자가 화면 진짜 가운데에 놓인다(뒤로가기는 이 화면에 없다: 앞 진단으로
          되돌아가면 이미 잰 값을 무엇으로 할지가 정해져 있지 않다). */}
      <header className="flex items-center">
        <span className="h-11 w-11 shrink-0" aria-hidden="true" />
        <p className="flex-1 text-center text-base font-semibold tabular-nums tracking-[-0.5px]">
          <span className="text-primary">{index + 1}</span>
          <span className="text-[var(--bk-400)]">{`/${total}`}</span>
        </p>
        <CloseDiagnosisButton />
      </header>

      {/* 시안의 아이콘 자리는 연분홍 원 플레이스홀더다(에셋 미확정). */}
      <div
        aria-hidden="true"
        className="mt-6 h-12 w-12 rounded-full bg-primary/15"
      />
      <h2 className="mt-4 text-[22px] font-semibold leading-[1.5] tracking-[-0.11px] text-[hsl(var(--title))]">
        {t(test.titleKey)}
      </h2>
      <p className="mt-2 whitespace-pre-line text-sm leading-[1.5] text-[var(--bk-500)]">
        {t(test.descKey)}
      </p>

      {/* 미리보기 카드 — 시안은 연분홍 테두리 안에 그 진단의 시각화 목업을 넣는다.
          테두리가 있어야 이 그림이 "지금 재는 값"이 아니라 예시임이 읽힌다. */}
      <div className="mt-6 rounded-2xl border border-primary/15 px-4 py-6">
        <GuideIllustration testId={test.id} />
      </div>

      {previewUrl && (
        <ReferencePreview
          src={previewUrl}
          labelKey={
            test.id === 'sustain'
              ? 'web.diagnosis.guide.previewTone'
              : 'web.diagnosis.guide.preview'
          }
          stopLabelKey={
            test.id === 'sustain'
              ? 'web.diagnosis.guide.previewToneStop'
              : 'web.diagnosis.guide.previewStop'
          }
        />
      )}

      <p className="mt-4 flex items-center gap-1.5 text-sm text-[var(--bk-500)]">
        <ClockCircleIcon className="h-4 w-4" aria-hidden="true" />
        {t('web.diagnosis.guide.duration', { value: duration })}
      </p>

      <button
        type="button"
        onClick={onStart}
        className="mb-8 mt-auto h-[50px] w-full rounded-full bg-[var(--bk-900)] text-base font-bold tracking-[0.08px] text-white"
      >
        {t('web.diagnosis.guide.start')}
      </button>
    </div>
  );
};
